import { useMemo } from "react";
import useSWR from "swr";

import { MeetingRoomDateRange } from "../../offering/order_params";
import { FBT, useI18n } from "../../lib/i18n/i18n_provider";
import { TimeSlot } from "../components/date-time-picker/checkout_datetime_picker";
import { getDuration } from "../utils/get_duration";
import {
  buildDynamicRatesQueryString,
  DynamicPricingAdjustmentType,
} from "./use_dynamic_rates_query";
import { DayRates } from "./use_best_day_rates_query";
import { hasSmartPricingSearchParam } from "../utils/has_smart_pricing_search_param";
import { formatCurrency } from "@/lib/currency/currency";

interface MinHourOption {
  type: "MinHour";
  minBookingUnit: number;
  unitPrice: number;
  adjustmentValue: number;
  adjustmentType: DynamicPricingAdjustmentType;
}

export interface HourRangeOption {
  type: "HourRange";
  startTime: string;
  endTime: string;
  unitPrice: number;
}

interface CurrentDynamicPriceRate {
  adjustmentType: DynamicPricingAdjustmentType;
  adjustmentValue: number;
}

interface BetterHourRateData {
  date: string;
  currentUnitPrice: number;
  originalUnitPrice: number;
  options: (MinHourOption | HourRangeOption)[];
  currentDynamicPriceRate?: CurrentDynamicPriceRate;
}

export function useGetBetterHourRatesQuery(
  timeSlots: TimeSlot[],
  unitPrice?: number,
  timeValues?: MeetingRoomDateRange,
  spaceId?: string,
  currencyCode?: string | null,
  spaceLayoutId?: string
) {
  const { date, startTime, endTime } = timeValues || {};
  const { fbt } = useI18n();

  let withTimeEndpoint: string | null = null;
  let withPriceEndpoint: string | null = null;
  if (spaceId) {
    if (date && startTime && endTime) {
      const params = new URLSearchParams();
      params.set("date", date);
      if (spaceLayoutId) {
        params.set("space_layout_id", spaceLayoutId);
      }
      if (hasSmartPricingSearchParam()) {
        params.set("smart_pricing", "true");
      }
      params.set("start_time", startTime);
      params.set("end_time", endTime);
      const qs = params.toString();
      withTimeEndpoint = `/api/v2/spaces/${spaceId}/dynamic_rates/better_hour_rates?&${qs}`;
    }
    if (date && unitPrice) {
      const qs = buildDynamicRatesQueryString({
        date,
        unitPrice,
        spaceLayoutId,
      });
      withPriceEndpoint = `/api/v2/spaces/${spaceId}/dynamic_rates/better_hour_rates?&${qs}`;
    }
  }

  const withTimeEndpointResponse = useSWR(withTimeEndpoint);
  const withPriceEndpointResponse = useSWR(withPriceEndpoint);

  const hasTimeSelection = !!(startTime && endTime);

  const betterHourRateByTime = toBetterHourRate(withTimeEndpointResponse.data);
  const betterHourRateByPrice = toBetterHourRate(
    withPriceEndpointResponse.data
  );

  const dynamicPrice: DayRates | undefined = useMemo(() => {
    if (
      betterHourRateByTime?.date &&
      betterHourRateByTime.currentUnitPrice &&
      unitPrice &&
      currencyCode
    ) {
      if (betterHourRateByTime.currentUnitPrice !== unitPrice) {
        return {
          [betterHourRateByTime.date]: betterHourRateByTime.currentUnitPrice,
        };
      } else {
        return undefined;
      }
    } else if (!hasTimeSelection) {
      return undefined;
    }
  }, [
    currencyCode,
    betterHourRateByTime?.currentUnitPrice,
    betterHourRateByTime?.date,
    unitPrice,
    hasTimeSelection,
  ]);

  const minHourOptions: MinHourOption[] = [];
  betterHourRateByTime?.options?.forEach((o) => {
    if (o.type === "MinHour") {
      minHourOptions.push(o);
    }
  });

  const hourRangeOptions: HourRangeOption[] = [];
  betterHourRateByPrice?.options?.forEach((o) => {
    if (o.type === "HourRange") {
      hourRangeOptions.push(o);
    }
  });

  const bulkHourRecommendation = getBulkHourRecommendation(
    fbt,
    minHourOptions,
    timeSlots,
    startTime,
    betterHourRateByTime?.currentDynamicPriceRate,
    currencyCode
  );

  const betterRateTimeSlots = getBetterRateTimeSlots(
    timeSlots,
    hourRangeOptions,
    betterHourRateByPrice?.originalUnitPrice
  );

  return {
    recommendation:
      hasTimeSelection && bulkHourRecommendation
        ? bulkHourRecommendation
        : undefined,
    timeSlots: betterRateTimeSlots,
    dynamicPrice,
  };
}

function getBulkHourRecommendation(
  fbt: FBT,
  options: MinHourOption[],
  timeSlots: TimeSlot[],
  startTime?: string,
  currentDynamicPriceRate?: CurrentDynamicPriceRate,
  currency?: string | null
) {
  let recommendation: string | undefined;
  const endTimeSlots = timeSlots.find(
    (s) => s.startTime === startTime
  )?.endTime;
  const finalEndTimeSlot =
    endTimeSlots && endTimeSlots[endTimeSlots.length - 1];
  const duration =
    startTime && finalEndTimeSlot
      ? getDuration(startTime, finalEndTimeSlot)
      : undefined;

  options.forEach((option) => {
    const isWithinMinBookingUnit = duration
      ? duration >= option.minBookingUnit
      : false;
    if (isWithinMinBookingUnit) {
      const savingDisplay =
        option.adjustmentType === DynamicPricingAdjustmentType.Percentage
          ? `${Math.abs(option.adjustmentValue)}%`
          : option.adjustmentType === DynamicPricingAdjustmentType.Amount &&
            currency
          ? formatCurrency(Math.abs(option.adjustmentValue), currency) +
            fbt("/hour")
          : undefined;
      if (savingDisplay) {
        recommendation = fbt(
          "Book " +
            fbt.param("numberOfHours", option.minBookingUnit) +
            "+ hours to unlock " +
            fbt.param("savingDisplay", savingDisplay) +
            " off."
        );
      }
    }
  });

  let currentSavingDisplay: string | undefined = undefined;
  if (currentDynamicPriceRate && currentDynamicPriceRate.adjustmentValue < 0) {
    if (
      currentDynamicPriceRate.adjustmentType ===
      DynamicPricingAdjustmentType.Percentage
    ) {
      currentSavingDisplay = `${Math.abs(
        currentDynamicPriceRate?.adjustmentValue
      )}%`;
    } else if (
      currentDynamicPriceRate?.adjustmentType ===
        DynamicPricingAdjustmentType.Amount &&
      currency
    ) {
      currentSavingDisplay =
        formatCurrency(
          Math.abs(currentDynamicPriceRate?.adjustmentValue),
          currency
        ) + fbt("/hour");
    }
  }

  if (currentSavingDisplay) {
    recommendation =
      fbt(
        "You're saving " +
          fbt.param("savingDisplay", currentSavingDisplay) +
          "!"
      ) + (recommendation ? ` ${recommendation}` : "");
  }

  return recommendation;
}

export function getBetterRateTimeSlots(
  timeSlots: TimeSlot[],
  hourRangeOptions: HourRangeOption[],
  originalUnitPrice?: number
) {
  const betterRateTimeSlots = timeSlots.map((s) => ({ ...s }));
  betterRateTimeSlots.forEach((slot) => {
    const startTime = Number(slot.startTime);
    hourRangeOptions.forEach((option) => {
      if (
        startTime >= Number(option.startTime) &&
        startTime <= Number(option.endTime) &&
        originalUnitPrice
      ) {
        if (option.unitPrice < originalUnitPrice) {
          slot.hasBetterRate = true;
        } else if (option.unitPrice > originalUnitPrice) {
          slot.hasHigherRate = true;
        }
      }
    });

    if (slot.price && originalUnitPrice && slot.price < originalUnitPrice) {
      slot.hasBetterRate = true;
    }
  });

  return betterRateTimeSlots;
}

function toBetterHourRate(data: any): BetterHourRateData | undefined {
  if (data) {
    return {
      date: data.date,
      originalUnitPrice: data.original_unit_price,
      currentUnitPrice: data.current_unit_price,
      options: data.options?.map((o: any) => {
        if (o.min_booking_unit) {
          return {
            type: "MinHour",
            minBookingUnit: Number(o.min_booking_unit),
            unitPrice: o.unit_price,
            adjustmentValue: Number(o.adjustment_value),
            adjustmentType: o.adjustment_type,
          };
        } else if (o.start_time && o.end_time) {
          return {
            type: "HourRange",
            startTime: o.start_time,
            endTime: o.end_time,
            unitPrice: o.unit_price,
          };
        }
        return undefined;
      }),
      currentDynamicPriceRate: data.current_dynamic_price_rate
        ? {
            adjustmentType: data.current_dynamic_price_rate.adjustment_type,
            adjustmentValue: Number(
              data.current_dynamic_price_rate.adjustment_value
            ),
          }
        : undefined,
    };
  }
}
